"use client";

import { useState } from "react";
import {
  EmailVerificationForm,
  emailVerificationFormSchema,
  EmailVerificationFormSchema,
  EmailVerificationValues,
} from "@/components/forms/EmailVerificationForm";
import CommsorLink from "@/components/CommsorLink";
import { Form } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { authClient, emailOtp } from "@/lib/auth-client";
import { useCooldown } from "@/hooks";
import { toast } from "@/components/ui/toast";

type AuthEmailVerificationFormProps = {
  email: string;
  onSuccess: () => void | Promise<void>;
  onError?: () => void | Promise<void>;
};

export default function AuthEmailVerificationForm({
  email,
  onSuccess,
  onError,
}: AuthEmailVerificationFormProps) {
  const verificationForm = useForm<EmailVerificationFormSchema>({
    resolver: zodResolver(emailVerificationFormSchema),
    defaultValues: { pin: "" },
  });
  const [isLoading, setIsLoading] = useState(false);

  const formId = "email-verification-form";

  const { isOnCooldown } = useCooldown(60, true);

  return (
    <div className="flex flex-col w-full">
      <Form {...verificationForm}>
        <EmailVerificationForm
          id={formId}
          onSuccess={async (data: EmailVerificationValues) => {
            setIsLoading(true);
            const { data: signInData, error: signInError } = await authClient.signIn.emailOtp({
              email: email,
              otp: data.pin,
            });

            if (signInData && !signInError) {
              if (onSuccess) {
                await onSuccess();
              }
            } else {
              verificationForm.setError("pin", {
                type: "manual",
                message: "Invalid OTP. Please try again.",
              });
              setIsLoading(false);
            }
          }}
          onError={async () => {
            if (onError) {
              await onError();
            }
            setIsLoading(false);
          }}
        >
          <h1 className="heading-2xl text-primary">Check your email</h1>
          <p className="mb-8 text-secondary body-lg">
            {email ? (
              <>
                Confirm your email address by entering the six-digit code sent to{" "}
                <span className="font-medium">{email}</span>.
              </>
            ) : (
              <>Confirm your email address by entering the six-digit code sent to your email.</>
            )}
          </p>
        </EmailVerificationForm>
        <Button
          type="submit"
          variant="primary"
          className="w-full my-3"
          form={formId}
          isLoading={isLoading}
          disabled={isLoading}
        >
          Continue
        </Button>
        {isOnCooldown && (
          <div className="flex gap-2 body-lg">
            <p className="text-secondary">Didn&apos;t get the code?</p>
            <CommsorLink
              onClick={async () => {
                const { data, error } = await emailOtp.sendVerificationOtp({
                  email: email,
                  type: "sign-in",
                });
                if (!data || error) {
                  toast.error(
                    error?.message ??
                      "We were unable to resend the code. Please try again later, or contact support."
                  );
                  return;
                }
              }}
            >
              Resend
            </CommsorLink>
          </div>
        )}
      </Form>
    </div>
  );
}
